/**
 * Booking mutations.
 *
 * ALL NON-OPTIMISTIC: bookings drive billing and PMS confirmation; a failed
 * optimistic update on a confirm/cancel would be trust-damaging (see
 * api-client-query doc §"Mutations and optimistic updates"). Every mutation
 * waits for the backend, writes the returned canonical booking into the detail
 * cache, and invalidates the list.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import { type Booking, bookingSchema } from '../responses';
import type { CreateBookingBody } from './_types';

function useBookingCacheSync(tenantId: string, hotelId: string) {
  const queryClient = useQueryClient();
  return (booking: Booking) => {
    // Write the returned canonical booking for an instant UI update, then
    // invalidate BOTH the booking detail and the list so they re-fetch
    // authoritatively (confirm/cancel are billing-affecting — never trust a
    // single response over the REST source of truth).
    queryClient.setQueryData(queryKeys.booking(tenantId, hotelId, booking.id), booking);
    void queryClient.invalidateQueries({
      queryKey: queryKeys.booking(tenantId, hotelId, booking.id),
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.bookings(tenantId, hotelId) });
  };
}

/** `POST /bookings` — create a draft booking. */
export function useCreateBooking(
  tenantId: string,
  hotelId: string,
): UseMutationResult<Booking, unknown, CreateBookingBody> {
  const client = useApiClient();
  const sync = useBookingCacheSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async (body: CreateBookingBody) => {
      const result = await client.POST('/api/v1/tenants/{tenantId}/hotels/{hotelId}/bookings', {
        params: { path: { tenantId, hotelId } },
        body,
        parseAs: 'json',
      });
      return unwrap(result, bookingSchema);
    },
    onSuccess: sync,
  });
}

/** Internal: a booking lifecycle transition (submit/confirm/cancel) by id. */
function useBookingLifecycle(
  tenantId: string,
  hotelId: string,
  segment: 'submit' | 'confirm' | 'cancel',
): UseMutationResult<Booking, unknown, { bookingId: string }> {
  const client = useApiClient();
  const sync = useBookingCacheSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ bookingId }: { bookingId: string }) => {
      const path = `/api/v1/tenants/{tenantId}/hotels/{hotelId}/bookings/{bookingId}/${segment}` as
        | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/bookings/{bookingId}/submit'
        | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/bookings/{bookingId}/confirm'
        | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/bookings/{bookingId}/cancel';
      const result = await client.POST(path, {
        params: { path: { tenantId, hotelId, bookingId } },
        parseAs: 'json',
      });
      return unwrap(result, bookingSchema);
    },
    onSuccess: sync,
  });
}

/** `POST /bookings/:bookingId/submit` — submit a draft for approval/PMS. */
export function useSubmitBooking(tenantId: string, hotelId: string) {
  return useBookingLifecycle(tenantId, hotelId, 'submit');
}

/** `POST /bookings/:bookingId/confirm` — confirm a booking (billing-affecting). */
export function useConfirmBooking(tenantId: string, hotelId: string) {
  return useBookingLifecycle(tenantId, hotelId, 'confirm');
}

/** `POST /bookings/:bookingId/cancel`. */
export function useCancelBooking(tenantId: string, hotelId: string) {
  return useBookingLifecycle(tenantId, hotelId, 'cancel');
}
